import React, { useMemo, useState, useCallback } from 'react';
import { Slate, Editable, withReact, useSlate, ReactEditor } from 'slate-react';
import { createEditor, Transforms, Text, Editor } from 'slate';
import { withHistory } from 'slate-history';
import PropTypes from 'prop-types';
import isHotkey from 'is-hotkey';
import uploadToDropbox from '../../../services/dropboxService';
import Toolbar from './Toolbar';

const DEFAULT_EDITOR_VALUE = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

// Function to clean URLs in content
const cleanContentUrls = (content) => {
  if (!content) return DEFAULT_EDITOR_VALUE;

  try {
    // If content is a string (HTML), clean the escaped characters
    if (typeof content === 'string') {
      // Remove all backslashes from the content string
      const unescapedContent = content.replace(/\\+/g, '');

      const parser = new DOMParser();
      const doc = parser.parseFromString(unescapedContent, 'text/html');

      // Clean video sources
      doc.querySelectorAll('video source').forEach(source => {
        source.src = source.src.replace(/\\+/g, '');
      });

      // Clean image sources
      doc.querySelectorAll('img').forEach(img => {
        img.src = img.src.replace(/\\+/g, '');
      });

      return doc.body.innerHTML;
    }

    // If content is Slate format (array of nodes)
    if (Array.isArray(content)) {
      return content.map(node => {
        if (node.type === 'image' || node.type === 'video') {
          return {
            ...node,
            url: node.url?.replace(/\\+/g, ''),
          };
        }
        return node;
      });
    }

    return content;
  } catch (error) {
    console.error('Error cleaning content URLs:', error);
    return DEFAULT_EDITOR_VALUE;
  }
};


const CircularProgress = ({ progress, size = 60, strokeWidth = 6 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-zinc-700"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-blue-600 transition-all duration-200"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-medium text-white">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

const UploadProgressDialog = ({ progress, type }) => {
  if (!progress || progress <= 0) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-zinc-800 p-8 rounded-lg shadow-xl flex flex-col items-center">
        <h3 className="text-xl font-semibold text-white mb-6">Uploading {type}...</h3>
        <CircularProgress progress={progress} />
      </div>
    </div>
  );
};

const ResizableMedia = ({ element, attributes, children, type }) => {
  const editor = useSlate();
  const { url, width = 400, height = type === 'video' ? 225 : 'auto' } = element;
  const [size, setSize] = useState({ width, height });
  const [isResizing, setIsResizing] = useState(false);
  const [showResizeHandle, setShowResizeHandle] = useState(false);

  const handleClick = (e) => {
    if (isResizing) return;
    e.preventDefault();
    e.stopPropagation();
    if (element.onClick) {
      element.onClick(element);
    }
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = size.width;

    const onMouseMove = (e) => {
      const delta = e.clientX - startX;
      const newWidth = Math.max(100, startWidth + delta);
      const newHeight = type === 'video' ? newWidth * 9 / 16 : 'auto';
      setSize({ width: newWidth, height: newHeight });

      Transforms.setNodes(
        editor,
        { width: newWidth, height: newHeight },
        { at: ReactEditor.findPath(editor, element) }
      );
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      {...attributes}
      contentEditable={false}
      className="relative my-4 inline-block group"
      style={{ width: size.width }}
      onClick={handleClick}
      onMouseEnter={() => setShowResizeHandle(true)}
      onMouseLeave={() => setShowResizeHandle(false)}
    >
      {type === 'image' ? (
        <img src={url} alt="" style={{ width: '100%', height: size.height }} className="rounded-lg" />
      ) : (
        <video src={url} controls className="rounded-lg w-full" style={{ height: size.height }} />
      )}
      
      {/* Resize handle */}
      <div
        className={`absolute bottom-0 right-0 w-6 h-6 bg-red-600 rounded-bl-lg cursor-ew-resize transition-opacity duration-200 flex items-center justify-center ${
          showResizeHandle ? 'opacity-100' : 'opacity-0'
        }`}
        onMouseDown={startResize}
      >
        <svg
          className="w-4 h-4 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l4-4 4 4m0 6l-4 4-4-4"
          />
        </svg>
      </div>

      {/* Size indicator */}
      {isResizing && (
        <div className="absolute top-0 left-0 bg-black/75 text-white px-2 py-1 rounded text-sm">
          {Math.round(size.width)}px × {Math.round(size.height)}px
        </div>
      )}

      {children}
    </div>
  );
};

const RichTextEditor = ({ onChange, initialContent, onMediaClick }) => {
  const editor = useMemo(() => withImages(withVideos(withHistory(withReact(createEditor())))), []);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadState, setUploadState] = useState({ progress: 0, type: null });

  const [value, setValue] = useState(() => {
    const content = cleanContentUrls(initialContent);
    // Add onClick handler to media elements
    return content.map(node => {
      if (node.type === 'image' || node.type === 'video') {
        return {
          ...node,
          onClick: onMediaClick
        };
      }
      return node;
    });
  });

  const handleChange = useCallback((newValue) => {
    // Preserve onClick handlers when content changes
    const contentWithHandlers = newValue.map(node => {
      if (node.type === 'image' || node.type === 'video') {
        return {
          ...node,
          onClick: onMediaClick
        };
      }
      return node;
    });
    setValue(contentWithHandlers);
    if (onChange) onChange(contentWithHandlers);
  }, [onChange, onMediaClick]);

  const toggleFormat = useCallback((format) => {
    const isActive = isFormatActive(editor, format);
    Transforms.setNodes(
      editor,
      { [format]: isActive ? null : true },
      { match: Text.isText, split: true }
    );
  }, [editor]);

  const handleKeyDown = useCallback((event) => {
    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault();
        toggleFormat(HOTKEYS[hotkey]);
      }
    }
  }, [toggleFormat]);

  const handleInsertFile = async (accept, type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = async (e) => {
      const file = e.target?.files?.[0];
      if (file) {
        try {
          setIsUploading(true);
          setUploadState({ progress: 0, type });
          
          const url = await uploadToDropbox(file, (progress) => {
            setUploadState(prev => ({ ...prev, progress }));
          });

          Transforms.insertNodes(editor, {
            type,
            url,
            width: 400,
            height: type === 'video' ? 225 : 'auto',
            children: [{ text: '' }],
          });
        } catch (err) {
          console.error(`${type} upload failed`, err);
        } finally {
          setIsUploading(false);
          setUploadState({ progress: 0, type: null });
        }
      }
    };
    input.click();
  };

  return (
    <div className="editor-container bg-zinc-800 rounded-lg overflow-hidden">
      <UploadProgressDialog progress={uploadState.progress} type={uploadState.type} />
      <Slate editor={editor} initialValue={value} onChange={handleChange}>
        <Toolbar
          isUploading={isUploading}
          toggleFormat={toggleFormat}
          insertImage={() => handleInsertFile('image/*', 'image')}
          insertVideo={() => handleInsertFile('video/*', 'video')}
        />
        <Editable
          className="prose prose-invert max-w-none min-h-[300px] focus:outline-none p-4"
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder="Start typing..."
          onKeyDown={handleKeyDown}
          spellCheck
          autoFocus
        />
      </Slate>
    </div>
  );
};

const renderElement = (props) => {
  const { element } = props;
  switch (element.type) {
    case 'image':
    case 'video':
      return <ResizableMedia {...props} type={element.type} />;
    case 'heading':
      return <h2 {...props.attributes} className="text-2xl font-bold my-4">{props.children}</h2>;
    default:
      return <p {...props.attributes} className="my-2">{props.children}</p>;
  }
};

const renderLeaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

const withImages = (editor) => {
  const { isVoid } = editor;
  editor.isVoid = (element) => element.type === 'image' || isVoid(element);
  return editor;
};

const withVideos = (editor) => {
  const { isVoid } = editor;
  editor.isVoid = (element) => element.type === 'video' || isVoid(element);
  return editor;
};

const isFormatActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n[format] === true,
    universal: true,
  });
  return !!match;
};

export default RichTextEditor;